/**
 * Version Check Service
 * Periodically checks for new app versions and notifies via callback when detected
 */

class VersionCheckService {
  constructor(options = {}) {
    this.checkInterval = options.checkInterval || 30 * 60 * 1000; // Default: 10 minutes
    this.versionUrl = options.versionUrl || '/app/version.json';
    this.currentVersion = null;
    this.checkTimer = null;
    this.isChecking = false;
    this.onNewVersion = options.onNewVersion || null; // Callback when new version is detected
    this.notifiedVersion = null; // Track which version we've already notified about
  }

  /**
   * Start periodic version checking
   */
  start() {
    if (this.checkTimer) {
      return; // Already started
    }

    // Perform initial check after a short delay
    setTimeout(() => this.checkVersion(), 1000);

    // Set up periodic checks
    this.checkTimer = setInterval(() => {
      this.checkVersion();
    }, this.checkInterval);
  }

  /**
   * Stop version checking
   */
  stop() {
    if (this.checkTimer) {
      clearInterval(this.checkTimer);
      this.checkTimer = null;
    }
  }

  /**
   * Set the callback for when a new version is detected
   * @param {Function} callback - Callback function to call when new version is detected
   */
  setOnNewVersionCallback(callback) {
    this.onNewVersion = callback;
    // Reset notified version when callback is set, so we can notify again if needed
    this.notifiedVersion = null;
  }

  /**
   * Perform a single version check
   * @returns {Promise<void>}
   */
  async checkVersion() {
    if (this.isChecking) {
      return; // Already checking, skip this cycle
    }

    this.isChecking = true;

    try {
      // Fetch version file with cache-busting query parameter
      const url = `${this.versionUrl}?t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const newVersion = data.version;

      if (!newVersion) {
        console.warn('[VersionCheck] Version file missing version field');
        return;
      }

      // First check - just store the version
      if (this.currentVersion === null) {
        this.currentVersion = newVersion;
        return;
      }

      // Compare versions
      if (this.currentVersion !== newVersion) {
        // Only notify once per new version
        if (this.notifiedVersion !== newVersion) {
          this.notifiedVersion = newVersion;
          // Notify about new version via callback
          if (this.onNewVersion && typeof this.onNewVersion === 'function') {
            this.onNewVersion();
          }
        }
      }
    } catch (error) {
      // Silently handle errors - network issues, etc.
      // Don't spam console in production
      if (process.env.NODE_ENV === 'development') {
        console.warn('[VersionCheck] Error checking version:', error.message);
      }
    } finally {
      this.isChecking = false;
    }
  }
}

export default VersionCheckService;
